import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import Select from "react-select";
import {
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";

import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  createDunningProfile,
  createDunningProfileSuccess,
  updateDunningProfile,
  updateDunningProfileSuccess,
  deleteDunningProfile,
  deleteDunningProfileSuccess,
  getDunningProfileList,
  getDunningProfileStepList,
  createDunningProfileStep,
  createDunningProfileStepSuccess,
  updateDunningProfileStep,
  updateDunningProfileStepSuccess,
  deleteDunningProfileStep,
  deleteDunningProfileStepSuccess,
} from "../../../../store/dunning/action";
import { getStaffList } from "../../../../store/staff/action";
import filterActiveStaff from "../../../../Components/Common/staffActive";

const CHANNEL_OPTIONS = [
  { value: "EMAIL", label: "Email" },
  { value: "SMS", label: "SMS" },
  { value: "PHONECALL", label: "Phone Call" },
  { value: "LETTER", label: "Letter" },
];

const CHANNEL_META = {
  EMAIL: { icon: "ri-mail-line", color: "primary" },
  SMS: { icon: "ri-message-2-line", color: "warning" },
  PHONECALL: { icon: "ri-phone-line", color: "danger" },
  LETTER: { icon: "ri-mail-send-line", color: "info" },
};

function StepTimeline({ steps, repeatIntervalDays }) {
  if (steps.length === 0) return null;
  return (
    <div className="dunning-step-timeline mb-3">
      {steps.map((step, index) => {
        const meta = CHANNEL_META[step.Channel] ?? CHANNEL_META.EMAIL;
        const gap =
          index > 0
            ? Number(step.DaysOverdueTrigger) -
              Number(steps[index - 1].DaysOverdueTrigger)
            : 0;
        return (
          <React.Fragment key={step.Id ?? step._key ?? index}>
            {index > 0 && (
              <div className="dunning-step-timeline__connector">
                {gap > 0 && <span>+{gap}d</span>}
              </div>
            )}
            <div
              className={`dunning-step-timeline__node${
                Number(step.FlagActive) === 1 ? "" : " is-inactive"
              }`}
            >
              <div
                className={`dunning-step-timeline__icon bg-soft-${meta.color} text-${meta.color}`}
              >
                <i className={meta.icon} />
              </div>
              <div className="dunning-step-timeline__day">
                Day {step.DaysOverdueTrigger}
              </div>
              <div className="dunning-step-timeline__name" title={step.Name}>
                {step.Name}
              </div>
            </div>
          </React.Fragment>
        );
      })}
      {repeatIntervalDays > 0 && (
        <>
          <div className="dunning-step-timeline__connector dunning-step-timeline__connector--repeat" />
          <div className="dunning-step-timeline__node">
            <div className="dunning-step-timeline__icon bg-soft-secondary text-secondary">
              <i className="ri-repeat-2-line" />
            </div>
            <div className="dunning-step-timeline__day">Repeats</div>
            <div className="dunning-step-timeline__name">
              every {repeatIntervalDays} days
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const emptyProfileForm = {
  Name: "",
  Description: "",
  FlagDefault: 0,
  FlagActive: 1,
  FlagRepeatFinalStep: 0,
  RepeatFinalStepIntervalDays: "",
};

const emptyStepForm = {
  StepNumber: 1,
  Name: "",
  DaysOverdueTrigger: "",
  Channel: "EMAIL",
  CommunicationCode: "",
  TaskStaff_Id: "",
  TaskTitle: "",
  TaskDescription: "",
  PostalTemplate_Id: "",
  DunningEventAction_Id: "",
  FlagActive: 1,
};

export default function DunningProfiles() {
  const dispatch = useDispatch();

  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [profileForm, setProfileForm] = useState(emptyProfileForm);

  const [stepModal, setStepModal] = useState(false);
  const [editingStepId, setEditingStepId] = useState(null);
  const [stepForm, setStepForm] = useState(emptyStepForm);

  // Steps queued locally for a not-yet-saved profile; sent with the create
  // call as dunningprofilestep_list so profile + steps land in one hit.
  const [pendingSteps, setPendingSteps] = useState([]);
  const nextPendingKey = useRef(1);

  const [deleteModal, setDeleteModal] = useState(false);
  // { type: "profile" | "step", id }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const {
    dunningProfileListAPI,
    dunningProfileStepListAPI,
    dunningActionListAPI,
    staffData,
    createProfileResponse,
    updateProfileResponse,
    deleteProfileResponse,
    createStepResponse,
    updateStepResponse,
    deleteStepResponse,
  } = useSelector((state) => ({
    dunningProfileListAPI:
      state.Dunning.dunningProfileList?.dunningprofile_list,
    dunningProfileStepListAPI:
      state.Dunning.dunningProfileStepList?.dunningprofilestep_list,
    dunningActionListAPI:
      state.Dunning.dunningActionList?.dunningeventaction_list,
    staffData: state.Staff.staffList?.staff_list,
    createProfileResponse: state.Dunning.createDunningProfileResponse,
    updateProfileResponse: state.Dunning.updateDunningProfileResponse,
    deleteProfileResponse: state.Dunning.deleteDunningProfileResponse,
    createStepResponse: state.Dunning.createDunningProfileStepResponse,
    updateStepResponse: state.Dunning.updateDunningProfileStepResponse,
    deleteStepResponse: state.Dunning.deleteDunningProfileStepResponse,
  }));

  const profileList = dunningProfileListAPI ?? [];
  const stepList = useMemo(
    () =>
      (dunningProfileStepListAPI ?? []).filter(
        (step) => Number(step?.DunningProfile_Id) === Number(selectedProfileId)
      ),
    [dunningProfileStepListAPI, selectedProfileId]
  );

  // What the steps table and timeline render: pending steps for a new
  // profile, saved steps otherwise — always in chase order (days overdue asc).
  const displayedSteps = useMemo(
    () =>
      [...(isNewProfile ? pendingSteps : stepList)].sort(
        (a, b) =>
          Number(a.DaysOverdueTrigger) - Number(b.DaysOverdueTrigger) ||
          Number(a.StepNumber) - Number(b.StepNumber)
      ),
    [isNewProfile, pendingSteps, stepList]
  );

  const selectedProfile = profileList.find(
    (p) => Number(p.Id) === Number(selectedProfileId)
  );

  useEffect(() => {
    dispatch(getDunningProfileList());
    dispatch(getStaffList());
  }, []);

  useEffect(() => {
    if (!selectedProfileId) return;
    dispatch(
      getDunningProfileStepList({ DunningProfile_Id: selectedProfileId })
    );
  }, [selectedProfileId]);

  // Keep the profile form in sync with the selected profile
  useEffect(() => {
    if (isNewProfile) return;
    if (!selectedProfile) {
      setProfileForm(emptyProfileForm);
      return;
    }
    setProfileForm({
      Name: selectedProfile.Name ?? "",
      Description: selectedProfile.Description ?? "",
      FlagDefault: Number(selectedProfile.FlagDefault) ? 1 : 0,
      FlagActive: Number(selectedProfile.FlagActive) ? 1 : 0,
      FlagRepeatFinalStep: Number(selectedProfile.FlagRepeatFinalStep) ? 1 : 0,
      RepeatFinalStepIntervalDays:
        selectedProfile.RepeatFinalStepIntervalDays ?? "",
    });
  }, [selectedProfile, isNewProfile]);

  const handleSelectProfile = (id) => {
    setIsNewProfile(false);
    setSelectedProfileId(id);
  };

  const handleNewProfile = () => {
    setIsNewProfile(true);
    setSelectedProfileId(null);
    setProfileForm(emptyProfileForm);
    setPendingSteps([]);
  };

  const setProfileField = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = () => {
    if (!profileForm.Name?.trim()) {
      toast.error("Profile name is required");
      return;
    }
    const payload = {
      Name: profileForm.Name,
      Description: profileForm.Description,
      FlagDefault: profileForm.FlagDefault,
      FlagActive: profileForm.FlagActive,
      FlagRepeatFinalStep: profileForm.FlagRepeatFinalStep,
      RepeatFinalStepIntervalDays: profileForm.FlagRepeatFinalStep
        ? Number(profileForm.RepeatFinalStepIntervalDays) || 0
        : 0,
    };
    if (isNewProfile) {
      const createPayload = { ...payload };
      if (pendingSteps.length > 0) {
        createPayload.dunningprofilestep_list = pendingSteps.map(
          ({ _key, ...step }) => step
        );
      }
      dispatch(createDunningProfile([createPayload]));
    } else {
      dispatch(updateDunningProfile([{ Id: selectedProfileId, ...payload }]));
    }
  };

  // Steps
  const setStepField = (field, value) => {
    setStepForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewStep = () => {
    const nextStepNumber =
      displayedSteps.length > 0
        ? Math.max(...displayedSteps.map((s) => Number(s.StepNumber) || 0)) + 1
        : 1;
    setEditingStepId(null);
    setStepForm({ ...emptyStepForm, StepNumber: nextStepNumber });
    setStepModal(true);
  };

  const handleEditStep = (step) => {
    setEditingStepId(step.Id ?? step._key);
    setStepForm({
      StepNumber: step.StepNumber ?? 1,
      Name: step.Name ?? "",
      DaysOverdueTrigger: step.DaysOverdueTrigger ?? "",
      Channel: step.Channel ?? "EMAIL",
      CommunicationCode: step.CommunicationCode ?? "",
      TaskStaff_Id: step.TaskStaff_Id ?? "",
      TaskTitle: step.TaskTitle ?? "",
      TaskDescription: step.TaskDescription ?? "",
      PostalTemplate_Id: step.PostalTemplate_Id ?? "",
      DunningEventAction_Id: step.DunningEventAction_Id ?? "",
      FlagActive: Number(step.FlagActive) ? 1 : 0,
    });
    setStepModal(true);
  };

  const handleSaveStep = () => {
    if (!stepForm.Name?.trim()) {
      toast.error("Step name is required");
      return;
    }
    if (stepForm.DaysOverdueTrigger === "") {
      toast.error("Days overdue trigger is required");
      return;
    }
    const payload = {
      StepNumber: Number(stepForm.StepNumber) || 1,
      Name: stepForm.Name,
      DaysOverdueTrigger: Number(stepForm.DaysOverdueTrigger) || 0,
      Channel: stepForm.Channel,
      FlagActive: stepForm.FlagActive,
    };
    if (stepForm.DunningEventAction_Id) {
      payload.DunningEventAction_Id = Number(stepForm.DunningEventAction_Id);
    }
    if (stepForm.Channel === "EMAIL" || stepForm.Channel === "SMS") {
      payload.CommunicationCode = stepForm.CommunicationCode;
    }
    if (stepForm.Channel === "PHONECALL") {
      if (stepForm.TaskStaff_Id) {
        payload.TaskStaff_Id = Number(stepForm.TaskStaff_Id);
      }
      payload.TaskTitle = stepForm.TaskTitle;
      payload.TaskDescription = stepForm.TaskDescription;
    }
    if (stepForm.Channel === "LETTER" && stepForm.PostalTemplate_Id) {
      payload.PostalTemplate_Id = Number(stepForm.PostalTemplate_Id);
    }

    // New profile: steps only exist locally until the profile is created
    if (isNewProfile) {
      if (editingStepId != null) {
        setPendingSteps((prev) =>
          prev.map((s) =>
            s._key === editingStepId ? { _key: s._key, ...payload } : s
          )
        );
      } else {
        setPendingSteps((prev) => [
          ...prev,
          { _key: nextPendingKey.current++, ...payload },
        ]);
      }
      setStepModal(false);
      return;
    }

    if (editingStepId) {
      dispatch(updateDunningProfileStep([{ Id: editingStepId, ...payload }]));
    } else {
      dispatch(
        createDunningProfileStep([
          { DunningProfile_Id: selectedProfileId, ...payload },
        ])
      );
    }
  };

  // Delete (shared modal for profile and step)
  const handleDelete = () => {
    if (deleteTarget?.type === "profile") {
      dispatch(deleteDunningProfile([deleteTarget.id]));
    }
    if (deleteTarget?.type === "step") {
      dispatch(deleteDunningProfileStep([deleteTarget.id]));
    }
  };

  const refreshSteps = () => {
    if (selectedProfileId) {
      dispatch(
        getDunningProfileStepList({ DunningProfile_Id: selectedProfileId })
      );
    }
  };

  // Create profile success
  useEffect(() => {
    if (isEmpty(createProfileResponse)) return;
    if (createProfileResponse.status === 200) {
      toast.success(createProfileResponse.message);
      setIsNewProfile(false);
      setPendingSteps([]);
      dispatch(getDunningProfileList());
    } else {
      toast.error(createProfileResponse.message);
    }
    dispatch(createDunningProfileSuccess([]));
  }, [createProfileResponse]);

  // Update profile success
  useEffect(() => {
    if (isEmpty(updateProfileResponse)) return;
    if (updateProfileResponse.status === 200) {
      toast.success(updateProfileResponse.message);
      dispatch(getDunningProfileList());
    } else {
      toast.error(updateProfileResponse.message);
    }
    dispatch(updateDunningProfileSuccess([]));
  }, [updateProfileResponse]);

  // Delete profile success
  useEffect(() => {
    if (isEmpty(deleteProfileResponse)) return;
    if (deleteProfileResponse.status === 200) {
      toast.success(deleteProfileResponse.message);
      setDeleteModal(false);
      setDeleteTarget(null);
      setSelectedProfileId(null);
      dispatch(getDunningProfileList());
    } else {
      toast.error(deleteProfileResponse.message);
    }
    dispatch(deleteDunningProfileSuccess([]));
  }, [deleteProfileResponse]);

  // Create step success
  useEffect(() => {
    if (isEmpty(createStepResponse)) return;
    if (createStepResponse.status === 200) {
      toast.success(createStepResponse.message);
      setStepModal(false);
      refreshSteps();
    } else {
      toast.error(createStepResponse.message);
    }
    dispatch(createDunningProfileStepSuccess([]));
  }, [createStepResponse]);

  // Update step success
  useEffect(() => {
    if (isEmpty(updateStepResponse)) return;
    if (updateStepResponse.status === 200) {
      toast.success(updateStepResponse.message);
      setStepModal(false);
      refreshSteps();
    } else {
      toast.error(updateStepResponse.message);
    }
    dispatch(updateDunningProfileStepSuccess([]));
  }, [updateStepResponse]);

  // Delete step success
  useEffect(() => {
    if (isEmpty(deleteStepResponse)) return;
    if (deleteStepResponse.status === 200) {
      toast.success(deleteStepResponse.message);
      setDeleteModal(false);
      setDeleteTarget(null);
      refreshSteps();
    } else {
      toast.error(deleteStepResponse.message);
    }
    dispatch(deleteDunningProfileStepSuccess([]));
  }, [deleteStepResponse]);

  const staffOptions = filterActiveStaff(staffData ?? [], [
    stepForm.TaskStaff_Id,
  ]).map((item) => ({
    label: item?.FullName,
    value: item?.Id,
  }));
  const actionOptions = (dunningActionListAPI ?? []).map((item) => ({
    label: item?.Name,
    value: item?.Id,
  }));

  const channelBadge = (channel) => {
    const found = CHANNEL_OPTIONS.find((c) => c.value === channel);
    return found?.label ?? channel;
  };

  const showProfileForm = isNewProfile || selectedProfile;

  return (
    <div className="mt-2 pt-2">
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => {
            setDeleteModal(false);
            setDeleteTarget(null);
          }}
        />
      )}

      <Row>
        {/* Profile list */}
        <Col md={4} lg={3}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0">Profiles</h6>
            <Button color="success" size="sm" onClick={handleNewProfile}>
              <i className="ri-add-line align-bottom" /> New Profile
            </Button>
          </div>
          <div className="list-group">
            {profileList.length === 0 && !isNewProfile && (
              <div className="text-muted p-2">
                No dunning profiles yet. Create one to get started.
              </div>
            )}
            {isNewProfile && (
              <div className="list-group-item active">
                <span className="fst-italic">New profile…</span>
              </div>
            )}
            {profileList.map((profile) => (
              <button
                key={profile.Id}
                type="button"
                className={`list-group-item list-group-item-action ${
                  Number(profile.Id) === Number(selectedProfileId)
                    ? "active"
                    : ""
                }`}
                onClick={() => handleSelectProfile(profile.Id)}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span className="text-truncate">{profile.Name}</span>
                  <span className="d-flex gap-1 ms-1">
                    {Number(profile.FlagDefault) === 1 && (
                      <Badge color="info">Default</Badge>
                    )}
                    {Number(profile.FlagActive) !== 1 && (
                      <Badge color="secondary">Inactive</Badge>
                    )}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Col>

        {/* Profile detail + steps */}
        <Col md={8} lg={9}>
          {!showProfileForm ? (
            <div className="text-muted p-3">
              Select a profile on the left, or create a new one, to manage its
              details and steps.
            </div>
          ) : (
            <>
              <div className="border rounded p-3 mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="mb-0">
                    {isNewProfile ? "New Profile" : "Profile Details"}
                  </h6>
                  {!isNewProfile && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={() => {
                        setDeleteTarget({
                          type: "profile",
                          id: selectedProfileId,
                        });
                        setDeleteModal(true);
                      }}
                    >
                      <i className="ri-delete-bin-line align-bottom" /> Delete
                      Profile
                    </Button>
                  )}
                </div>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Name</Label>
                      <Input
                        type="text"
                        value={profileForm.Name}
                        onChange={(e) =>
                          setProfileField("Name", e.target.value)
                        }
                        placeholder="e.g. Standard Debt Chase"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Description</Label>
                      <Input
                        type="textarea"
                        rows={2}
                        value={profileForm.Description}
                        onChange={(e) =>
                          setProfileField("Description", e.target.value)
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <Col md="auto">
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="profileFlagActive"
                        checked={profileForm.FlagActive === 1}
                        onChange={(e) =>
                          setProfileField(
                            "FlagActive",
                            e.target.checked ? 1 : 0
                          )
                        }
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="profileFlagActive"
                      >
                        Active
                      </Label>
                    </div>
                  </Col>
                  <Col md="auto">
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="profileFlagDefault"
                        checked={profileForm.FlagDefault === 1}
                        onChange={(e) =>
                          setProfileField(
                            "FlagDefault",
                            e.target.checked ? 1 : 0
                          )
                        }
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="profileFlagDefault"
                      >
                        Default profile
                      </Label>
                    </div>
                  </Col>
                  <Col md="auto">
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="profileFlagRepeat"
                        checked={profileForm.FlagRepeatFinalStep === 1}
                        onChange={(e) =>
                          setProfileField(
                            "FlagRepeatFinalStep",
                            e.target.checked ? 1 : 0
                          )
                        }
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="profileFlagRepeat"
                      >
                        Repeat final step
                      </Label>
                    </div>
                  </Col>
                  {profileForm.FlagRepeatFinalStep === 1 && (
                    <Col md={3}>
                      <div className="d-flex align-items-center gap-2">
                        <Label className="mb-0 text-nowrap">Every</Label>
                        <Input
                          type="number"
                          min={1}
                          value={profileForm.RepeatFinalStepIntervalDays}
                          onChange={(e) =>
                            setProfileField(
                              "RepeatFinalStepIntervalDays",
                              e.target.value
                            )
                          }
                        />
                        <Label className="mb-0">days</Label>
                      </div>
                    </Col>
                  )}
                </Row>
                <div className="mt-2">
                  <Button color="primary" size="sm" onClick={handleSaveProfile}>
                    {isNewProfile ? "Create Profile" : "Save Changes"}
                  </Button>
                  {isNewProfile && (
                    <Button
                      color="light"
                      size="sm"
                      className="ms-2"
                      onClick={() => {
                        setIsNewProfile(false);
                        setProfileForm(emptyProfileForm);
                        setPendingSteps([]);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Steps — saved via the API for existing profiles, queued
                  locally and sent with the create call for new ones */}
              <div className="border rounded p-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="mb-0">
                    Steps
                    {isNewProfile && (
                      <small className="text-muted fw-normal ms-2">
                        created together with the profile
                      </small>
                    )}
                  </h6>
                  <Button color="success" size="sm" onClick={handleNewStep}>
                    <i className="ri-add-line align-bottom" /> Add Step
                  </Button>
                </div>
                {displayedSteps.length === 0 ? (
                  <div className="text-muted p-2">
                    No steps yet. Add the first step of this chase sequence.
                  </div>
                ) : (
                  <>
                    <StepTimeline
                      steps={displayedSteps}
                      repeatIntervalDays={
                        profileForm.FlagRepeatFinalStep === 1
                          ? Number(profileForm.RepeatFinalStepIntervalDays) || 0
                          : 0
                      }
                    />
                    <Table responsive size="sm" className="align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: "60px" }}>#</th>
                          <th>Name</th>
                          <th>Trigger (days overdue)</th>
                          <th>Channel</th>
                          <th>Action Type</th>
                          <th>Active</th>
                          <th style={{ width: "90px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedSteps.map((step) => (
                          <tr key={step.Id ?? step._key}>
                            <td>{step.StepNumber}</td>
                            <td>{step.Name}</td>
                            <td>{step.DaysOverdueTrigger}</td>
                            <td>
                              <Badge color="primary" pill>
                                {channelBadge(step.Channel)}
                              </Badge>
                            </td>
                            <td>
                              {
                                actionOptions.find(
                                  (o) =>
                                    Number(o.value) ===
                                    Number(step.DunningEventAction_Id)
                                )?.label
                              }
                            </td>
                            <td>
                              {Number(step.FlagActive) === 1 ? (
                                <Badge color="success">Yes</Badge>
                              ) : (
                                <Badge color="secondary">No</Badge>
                              )}
                            </td>
                            <td className="text-end">
                              <Button
                                color="light"
                                size="sm"
                                className="me-1"
                                title="Edit step"
                                onClick={() => handleEditStep(step)}
                              >
                                <i className="ri-pencil-line" />
                              </Button>
                              <Button
                                color="light"
                                size="sm"
                                title="Delete step"
                                onClick={() => {
                                  // Pending steps only exist locally — no
                                  // API call or confirm needed
                                  if (isNewProfile) {
                                    setPendingSteps((prev) =>
                                      prev.filter((s) => s._key !== step._key)
                                    );
                                    return;
                                  }
                                  setDeleteTarget({
                                    type: "step",
                                    id: step.Id,
                                  });
                                  setDeleteModal(true);
                                }}
                              >
                                <i className="ri-delete-bin-line text-danger" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </div>
            </>
          )}
        </Col>
      </Row>

      {/* Step create/edit modal */}
      <Modal
        isOpen={stepModal}
        toggle={() => setStepModal(!stepModal)}
        centered
      >
        <ModalHeader toggle={() => setStepModal(false)}>
          {editingStepId ? "Edit Step" : "Add Step"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col xs={4}>
              <FormGroup>
                <Label>Step #</Label>
                <Input
                  type="number"
                  min={1}
                  value={stepForm.StepNumber}
                  onChange={(e) => setStepField("StepNumber", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col xs={8}>
              <FormGroup>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={stepForm.Name}
                  onChange={(e) => setStepField("Name", e.target.value)}
                  placeholder="e.g. Gentle reminder email"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Label>Days Overdue Trigger</Label>
                <Input
                  type="number"
                  min={0}
                  value={stepForm.DaysOverdueTrigger}
                  onChange={(e) =>
                    setStepField("DaysOverdueTrigger", e.target.value)
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Label>Channel</Label>
                <Select
                  options={CHANNEL_OPTIONS}
                  value={CHANNEL_OPTIONS.find(
                    (o) => o.value === stepForm.Channel
                  )}
                  onChange={(option) => setStepField("Channel", option.value)}
                />
              </FormGroup>
            </Col>
          </Row>

          {(stepForm.Channel === "EMAIL" || stepForm.Channel === "SMS") && (
            <FormGroup>
              <Label>Communication Code</Label>
              <Input
                type="text"
                value={stepForm.CommunicationCode}
                onChange={(e) =>
                  setStepField("CommunicationCode", e.target.value)
                }
                placeholder="e.g. DUNNING_REMINDER_GENTLE"
              />
            </FormGroup>
          )}

          {stepForm.Channel === "PHONECALL" && (
            <>
              <FormGroup>
                <Label>Assign Task To</Label>
                <Select
                  options={staffOptions}
                  isClearable
                  value={
                    staffOptions.find(
                      (o) => Number(o.value) === Number(stepForm.TaskStaff_Id)
                    ) ?? null
                  }
                  onChange={(option) =>
                    setStepField("TaskStaff_Id", option?.value ?? "")
                  }
                />
              </FormGroup>
              <FormGroup>
                <Label>Task Title</Label>
                <Input
                  type="text"
                  value={stepForm.TaskTitle}
                  onChange={(e) => setStepField("TaskTitle", e.target.value)}
                  placeholder="e.g. Debt chase call — {{CustomerName}}"
                />
              </FormGroup>
              <FormGroup>
                <Label>Task Description</Label>
                <Input
                  type="textarea"
                  rows={3}
                  value={stepForm.TaskDescription}
                  onChange={(e) =>
                    setStepField("TaskDescription", e.target.value)
                  }
                />
                <small className="text-muted">
                  Placeholders: {"{{CustomerName}}"},{" "}
                  {"{{BalanceOverdueTotal}}"}, {"{{InvoiceCount}}"},{" "}
                  {"{{DaysOverdue}}"}
                </small>
              </FormGroup>
            </>
          )}

          {stepForm.Channel === "LETTER" && (
            <FormGroup>
              <Label>Postal Template Id</Label>
              <Input
                type="number"
                min={1}
                value={stepForm.PostalTemplate_Id}
                onChange={(e) =>
                  setStepField("PostalTemplate_Id", e.target.value)
                }
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Action Type</Label>
            <Select
              options={actionOptions}
              isClearable
              value={
                actionOptions.find(
                  (o) =>
                    Number(o.value) === Number(stepForm.DunningEventAction_Id)
                ) ?? null
              }
              onChange={(option) =>
                setStepField("DunningEventAction_Id", option?.value ?? "")
              }
            />
          </FormGroup>

          <div className="form-check form-switch">
            <Input
              type="checkbox"
              className="form-check-input"
              id="stepFlagActive"
              checked={stepForm.FlagActive === 1}
              onChange={(e) =>
                setStepField("FlagActive", e.target.checked ? 1 : 0)
              }
            />
            <Label className="form-check-label" htmlFor="stepFlagActive">
              Active
            </Label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setStepModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveStep}>
            {editingStepId ? "Save Changes" : "Add Step"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
